import { Component, WritableSignal, computed, inject, signal } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { catchError, filter, finalize, map, switchMap, takeUntil, tap, of } from 'rxjs';
import { IdTagStatusEnum } from 'src/app/shared/enums/id-tag-status.enum';
import { IdTagEditRequest, IdTagService, inactivateAndCreateRequest } from 'src/app/shared/services/id-tag.service';
import { Department } from '../../department/department.model';
import { TranslateIdTagStatusEnumPipe } from '../../../shared/pipes/translate-id-tag-status-enum.pipe';
import { AdmDepTypeaheadComponent } from 'src/app/shared/components/adm-dep-typeahead/adm-dep-typeahead.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DepartmentService } from 'src/app/shared/services/department.service';
import { FeedbackSuccessComponent, FeedbackInfoComponent } from 'ok-angular-shared/components';
import { AdmG4AgreementNumberComponent } from 'src/app/shared/components/adm-g4-agreement-number/adm-g4-agreement-number.component';
import { MatHint } from '@angular/material/select';
import { HttpErrorResponse } from '@angular/common/http';
import { SnackbarService } from 'ok-angular-shared/components';
import { TopLoaderService } from 'src/app/core/services/top-loader.service';
import { ErrorMessage } from 'src/app/shared/models/errorMessage.model';

interface SwitchDepartmentComponentForm {
  selectedDepartment: FormControl<Department | null>;
  agreementNumber: FormControl<number | null>;
}

@Component({
  selector: 'adm-id-tag-switch-department-modal',
  templateUrl: './id-tag-switch-department-modal.component.html',
  styleUrls: ['./id-tag-switch-department-modal.component.scss'],
  imports: [
    TranslateIdTagStatusEnumPipe,
    AdmDepTypeaheadComponent,
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    ReactiveFormsModule,
    FeedbackInfoComponent,
    FeedbackSuccessComponent,
    AdmG4AgreementNumberComponent,
    MatHint,
  ],
})
export class IdTagSwitchDepartmentModalComponent {
  dialogRef = inject<MatDialogRef<IdTagSwitchDepartmentModalComponent>>(MatDialogRef);
  data = inject<{
    idTagId: number;
    departmentName: string;
  }>(MAT_DIALOG_DATA);
  private idTagService = inject(IdTagService);
  private departmentService = inject(DepartmentService);
  private loadingService = inject(TopLoaderService);
  private snackbarService = inject(SnackbarService);
  public isInactivateAndCreate = false;
  public saving = signal<boolean>(false);
  public loading = signal<boolean>(false);
  public wasSaved = signal<boolean>(false);
  public savedId = signal<number | null>(null);
  public departmentSwitchForm = new FormGroup<SwitchDepartmentComponentForm>({
    selectedDepartment: new FormControl<Department | null>(null, { validators: [Validators.required] }),
    agreementNumber: new FormControl<number | null>(null, [Validators.required]),
  });

  isSubmitDisabled = computed(() => !this.formValid() || this.wasSaved());
  resetDepartmentTrigger: WritableSignal<{ reset: boolean }> = signal<{ reset: boolean }>({ reset: false });

  currentDepartment: WritableSignal<Department | null> = signal<Department | null>(null);
  departmentSelected = (department: Department | null) => {
    this.departmentSwitchForm.controls.selectedDepartment.setValue(department);
  };

  constructor() {
    this.departmentSwitchForm.controls.selectedDepartment.valueChanges
      .pipe(
        filter(x => x != null),
        switchMap(dep =>
          this.departmentService.getDepartment(dep?.id).pipe(
            tap(x => {
              this.departmentSwitchForm.controls.agreementNumber.setValue(x.agreementNumber ?? null);
            })
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  private formValid = toSignal(this.departmentSwitchForm.statusChanges.pipe(map(() => this.departmentSwitchForm.valid)), {
    initialValue: this.departmentSwitchForm.valid,
  });

  idTagSignal = toSignal(
    this.idTagService.getIdTag(this.data.idTagId).pipe(
      switchMap(idTag => {
        this.isInactivateAndCreate = idTag.status !== IdTagStatusEnum[IdTagStatusEnum.Pending];
        return this.departmentService.getDepartment(idTag.departmentId).pipe(
          tap(dep => {
            this.currentDepartment.set(dep);
          }),
          map(() => idTag)
        );
      })
    ),
    { initialValue: null }
  );

  public close() {
    if (this.wasSaved()) {
      this.dialogRef.close(this.idTagSignal());
    } else {
      this.dialogRef.close();
    }
  }

  public saveDepartmentSwitch() {
    this.saving.set(true);
    const departmentId = this.departmentSwitchForm.controls.selectedDepartment.value?.id;
    if (departmentId == null) {
      this.saving.set(false);
      return;
    }

    if (this.isInactivateAndCreate) {
      const idTag = this.idTagSignal();
      if (!idTag) {
        this.saving.set(false);
        return;
      }

      this.idTagService
        .inactivateAndCreate(idTag.id, {
          departmentId: departmentId,
          agreementNumber: this.departmentSwitchForm.controls.agreementNumber.value,
        } as inactivateAndCreateRequest)
        .pipe(
          catchError((httpError: HttpErrorResponse) => {
            if ((httpError.status == 400 && httpError.error) || (httpError.status == 404 && httpError.error)) {
              const error = httpError.error as ErrorMessage;
              if (error.code === 20003) {
                this.snackbarService.warning(`Samme afdeling er allerede tilknyttet denne brik.`);
              }
            } else {
              this.snackbarService.error('Der skete en fejl under skift af afdelingen.');
              this.dialogRef.close();
            }
            return of();
          }),
          finalize(() => this.loadingService.setLoading(false))
        )
        .subscribe({
          next: result => {
            this.savedId.set(result);
            this.wasSaved.set(true);
          },
        })
        .add(() => {
          this.loading.set(false);
        });
    } else {
      const idtag = this.idTagSignal();
      if (!idtag) {
        this.saving.set(false);
        this.loading.set(false);
        return;
      }

      idtag.departmentId = departmentId;
      const editIdTag = {
        departmentId: idtag.departmentId,
        darId: idtag.darId ? idtag.darId.toString() : null,
        shippingAddress: idtag.shippingAddress,
        externalReference: idtag.externalReference,
        description: idtag.description,
        attention: idtag.attention,
        agreementNumber: this.departmentSwitchForm.controls.agreementNumber.value,
      } as IdTagEditRequest;

      this.idTagService
        .editIdTag(idtag.id, editIdTag)
        .subscribe({
          next: result => {
            this.savedId.set(result);
            this.wasSaved.set(true);
          },
        })
        .add(() => {
          this.loading.set(false);
        });
    }
    this.saving.set(false);
  }
}
